import { ReactNode, useMemo } from 'react';
import { AgentApplicantAuthorisationStatus, ApplicantOfferRequirementTask, ApplicantOfferRequirementTaskLevel, ApplicantOfferRequirementTaskStatus, ApplicantOfferResponseStatus, ApplicantOfferStatus, ApplicantStatusReason, ApplicationStatus } from 'api-paths-cas';
import { getAllTasksFromRequirements } from '@/utilities/transformRequirements';
import { OfferTooltipLabels } from 'apply-data';
import {
  faAngleDown,
  faCheck,
  faInfo,
  faPen,
  faThumbsDown,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { AlertProps, BadgeProps, ButtonProps, useDisclosure } from '@chakra-ui/react';
import { getStatusByConditionType, Icon } from 'shared-components';

import { To, useLocation, useNavigate, useParams } from 'react-router-dom';
import { hasPerm, P } from '@/utilities/roles/permissions';
import { useSelector } from 'react-redux';
import { ApplicationState } from '@/store';
import { getRole } from '@/store/selectors';
import { UserRole } from '@/store/auth/types';

interface UseApplicantSidebarActionsProps {
  onIssueOfferOpen: () => void;
  onWithdrawRejectOpen: () => void;
  onReinstateOfferOpen: () => void;
  onRequestPreOfferTasksOpen?: () => void;
  onRequestEnrolmentTasksOpen?: () => void;
}

interface ActionButton {
  label: string;
  props: ButtonProps & { 'data-selector'?: string };
  onClick?: () => void;
  isPrimary?: boolean;
  isDropdown?: boolean;
  to?: To;
  menuItems?: Array<{
    label: string;
    onClick: () => void;
  }>;
}

export interface SidebarAlert {
  props: AlertProps & { 'data-selector'?: string };
  children: ReactNode;
}

export interface StatusIndicator {
  icon: IconDefinition;
  colorScheme: BadgeProps['colorScheme'];
  label: string;
  dataSelector: string;
  variant?: string;
  contained?: boolean;
  action?: {
    label: string;
    href: string;
  };
}

// Optional dependencies for testing
export interface UseApplicantSidebarActionsDeps {
  navigate?: ReturnType<typeof useNavigate>;
  location?: ReturnType<typeof useLocation>;
  params?: Record<string, string | undefined>;
  role?: UserRole;
  applyState?: ApplicationState['apply'];
  unlinkEmailDisclosure?: ReturnType<typeof useDisclosure>;
  canEditOffer?: boolean;
}

export const useApplicantSidebarActions = (
  {
    onIssueOfferOpen,
    onWithdrawRejectOpen,
    onReinstateOfferOpen,
    onRequestPreOfferTasksOpen,
    onRequestEnrolmentTasksOpen,
  }: UseApplicantSidebarActionsProps,
  deps?: UseApplicantSidebarActionsDeps,
): {
  buttons: ActionButton[];
  alerts: SidebarAlert[];
  statusIndicators: StatusIndicator[];
  unlinkEmailModalProps: {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
  };
  isInOffersPath: boolean;
} => {
  // Use injected dependencies or fall back to actual hooks
  const navigate = deps?.navigate ?? useNavigate();
  const location = deps?.location ?? useLocation();
  const params = deps?.params ?? useParams();
  const { userId, uni, applicationId } = params as { userId?: string; uni?: string; applicationId?: string };

  const role = deps?.role ?? useSelector(getRole);
  const applyState = deps?.applyState ?? useSelector((state: ApplicationState) => state.apply);
  const { application, offer, agentAccessStatus, applicant } = applyState || {};
  const { navigationUrls } = useSelector((state: ApplicationState) => state.config);
  const {
    isOpen: isUnlinkEmailOpen,
    onOpen: onUnlinkEmailOpen,
    onClose: onUnlinkEmailClose,
  } = deps?.unlinkEmailDisclosure ?? useDisclosure();

  const {
    status = ApplicantOfferStatus.Applied,
    requirements = [],
    issueDate,
    reissueDate,
    conditionType,
    offerLetter,
    statusReason,
  } = offer || {};

  // TODO ENROLY-3304: Remove Draft mapping after data migration completes
  const effectiveOfferStatus = useMemo(() => {
    if (status === ApplicantOfferStatus.Draft) {
      return ApplicantOfferStatus.Applied;
    }
    return status;
  }, [status]);

  const pdfUrl = offerLetter?.pdfUrl;

  const isNotSubmitted = [ApplicationStatus.InProgress, ApplicationStatus.Completed].includes(
    application?.applicationStatus,
  );

  const isInOffersPath = location.pathname.includes('/offers');
  const isInProgrammesPath = location.pathname.includes('/programmes');

  const applicantStatus = getStatusByConditionType(offer?.applicantStatus, conditionType);

  const isApplied = effectiveOfferStatus === ApplicantOfferStatus.Applied;
  const isConditional = effectiveOfferStatus === ApplicantOfferStatus.Conditional;
  const isUnconditional = effectiveOfferStatus === ApplicantOfferStatus.Unconditional;
  const isWithdrawn = effectiveOfferStatus === ApplicantOfferStatus.Withdrawn;
  const isRejected = effectiveOfferStatus === ApplicantOfferStatus.Rejected;
  const isIssuedOffer = isConditional || isUnconditional;
  const hasPDFUrl = pdfUrl && pdfUrl.length > 0;

  const canEditOffer = deps?.canEditOffer ?? hasPerm(role as UserRole, P.EDIT_APPLY_OFFER);
  const isAuthorisationDenied = agentAccessStatus === AgentApplicantAuthorisationStatus.AUTHORISATION_DENIED;

  // Extract all tasks from hierarchical requirements
  const allTasks = useMemo(() => getAllTasksFromRequirements(requirements), [requirements]);

  // Exclude StaffOnly tasks from all applicant-facing checks (they are internal and should not affect offer indicators)
  const nonStaffOnlyTasks = useMemo(
    () => allTasks.filter(task => task.type !== ApplicantOfferRequirementTask.StaffOnly),
    [allTasks],
  );

  // Exclude PreEnrolment tasks from offer-level checks (they don't block unconditional offers)
  const offerLevelTasks = useMemo(
    () =>
      nonStaffOnlyTasks.filter(
        task => (task?.pendingChanges?.level ?? task?.level) !== ApplicantOfferRequirementTaskLevel.PreEnrolment,
      ),
    [nonStaffOnlyTasks],
  );

  const { hasUnresolvedRequirements, notSatisfiedRequirementsCount } = useMemo(() => {
    const notSatisfiedCount = offerLevelTasks.filter(
      task => (task?.pendingChanges?.status ?? task?.status) === ApplicantOfferRequirementTaskStatus.NotSatisfied,
    ).length;

    return {
      hasUnresolvedRequirements: notSatisfiedCount > 0,
      notSatisfiedRequirementsCount: notSatisfiedCount,
    };
  }, [offerLevelTasks]);

  const allRequirementsResolved = useMemo(() => {
    const resolvedStatuses = [
      ApplicantOfferRequirementTaskStatus.Satisfied,
      ApplicantOfferRequirementTaskStatus.Provisional,
      ApplicantOfferRequirementTaskStatus.Exempt,
    ];
    return (
      offerLevelTasks.length === 0 ||
      offerLevelTasks.every(task => {
        const taskStatus = task?.pendingChanges?.status ?? task?.status;
        return resolvedStatuses.includes(taskStatus);
      })
    );
  }, [offerLevelTasks]);

  const conditionReqs = allTasks.filter(
    task => (task?.pendingChanges?.status ?? task?.status) === ApplicantOfferRequirementTaskStatus.Condition,
  );

  const hasRequirementsBeenEdited = nonStaffOnlyTasks.some(
    task => Object.keys(task?.pendingChanges ?? {}).length > 0 && !task?.lastUpdatedByApplicant,
  );
  const hasBeenEdited = Object.keys(offer?.pendingChanges ?? {}).length > 0 || hasRequirementsBeenEdited;

  const canIssueOffer = isApplied || (isIssuedOffer && hasBeenEdited);

  const hasPendingPreOfferTasks = useMemo(
    () =>
      nonStaffOnlyTasks.some(task => {
        const effectiveLevel = task?.pendingChanges?.level ?? task?.level;
        const isPreOffer = effectiveLevel === ApplicantOfferRequirementTaskLevel.PreOffer;
        return isPreOffer && Object.keys(task?.pendingChanges ?? {}).length > 0 && !task?.lastUpdatedByApplicant;
      }),
    [nonStaffOnlyTasks],
  );

  // Detects any enrolment task with staff-made pending changes (level changes, status assessments, etc.)
  const hasEnrolmentTaskEdits = useMemo(
    () =>
      nonStaffOnlyTasks.some(task => {
        const effectiveLevel = task?.pendingChanges?.level ?? task?.level;
        const isEnrolment = effectiveLevel === ApplicantOfferRequirementTaskLevel.PreEnrolment;
        const hasPendingChanges = Object.keys(task?.pendingChanges ?? {}).length > 0 && !task?.lastUpdatedByApplicant;
        return isEnrolment && hasPendingChanges;
      }),
    [nonStaffOnlyTasks],
  );

  // Pre-offer tasks that have been requested (active, not pending)
  const hasActivePreOfferTasks = nonStaffOnlyTasks.some(
    task => task.level === ApplicantOfferRequirementTaskLevel.PreOffer,
  );
  const preOfferTasksRequested = hasActivePreOfferTasks && !hasPendingPreOfferTasks;

  // Detect non-enrolment pending edits (condition-level or offer-level changes by staff)
  const hasNonEnrolmentTaskEdits = nonStaffOnlyTasks.some(task => {
    const hasPendingChanges = Object.keys(task?.pendingChanges ?? {}).length > 0 && !task?.lastUpdatedByApplicant;
    const effectiveLevel = task?.pendingChanges?.level ?? task?.level;
    const isEnrolment = effectiveLevel === ApplicantOfferRequirementTaskLevel.PreEnrolment;
    return !isEnrolment && hasPendingChanges;
  });
  const hasOfferLevelEdits = Object.keys(offer?.pendingChanges ?? {}).length > 0;
  const hasOnlyEnrolmentEdits = hasEnrolmentTaskEdits && !hasNonEnrolmentTaskEdits && !hasOfferLevelEdits;

  // "Complete assessment" state: Conditional + issued + applicant fulfilled conditions but uni hasn't assessed
  const unassessedConditionTasks = allTasks.filter(task => {
    const effectiveLevel = task?.pendingChanges?.level ?? task?.level;
    const isConditionLevel =
      effectiveLevel === ApplicantOfferRequirementTaskLevel.Condition ||
      effectiveLevel === ApplicantOfferRequirementTaskLevel.PreOffer;
    const effectiveStatus = task?.pendingChanges?.status ?? task?.status;
    const isUnassessed =
      effectiveStatus === ApplicantOfferRequirementTaskStatus.Condition ||
      effectiveStatus === ApplicantOfferRequirementTaskStatus.Assess;
    return isConditionLevel && isUnassessed;
  });
  const hasApplicantFulfilledUnassessedConditions =
    unassessedConditionTasks.length > 0 && unassessedConditionTasks.every(task => task?.lastUpdatedByApplicant);

  const showCompleteAssessment =
    isConditional && (issueDate || reissueDate) && !hasBeenEdited && hasApplicantFulfilledUnassessedConditions;

  const casUrl = useMemo((): string | null => {
    // isLocked is true if the offer has been imported to CAS Shield
    const isLocked = offer?.isLocked;
    const offerId = offer?.id;
    if (
      navigationUrls?.casStaff &&
      applicant?.studentNo &&
      offer?.intakeId &&
      offerId &&
      isLocked &&
      applicant?.importedToCasShield
    ) {
      return `${navigationUrls.casStaff}/student/${uni}/${applicant.studentNo}/${offer?.intakeId}/${offerId}`;
    }
    return null;
  }, [applicant, application, navigationUrls, uni, offer?.id, offer?.intakeId]);

  // Menu items for More actions dropdown
  const moreActionsMenuItems = useMemo(() => {
    const items = [
      {
        label: 'Unlink email address',
        onClick: onUnlinkEmailOpen,
      },
    ];
    if (casUrl) {
      items.push({
        label: 'Open record in CAS Shield',
        onClick: () => globalThis.window.open(casUrl),
      });
    }
    return items;
  }, [onUnlinkEmailOpen, casUrl]);

  const primaryActionButton = useMemo((): ActionButton | undefined => {
    if (!isInOffersPath) {
      return {
        label: 'View programmes',
        props: {},
        to: `/application/${uni}/${userId}/${applicationId}/programmes`,
      };
    }

    if (hasPendingPreOfferTasks && isApplied && onRequestPreOfferTasksOpen) {
      return {
        label: 'Request pre-offer tasks',
        props: { variant: 'solid' },
        onClick: onRequestPreOfferTasksOpen,
      };
    }

    if (
      hasEnrolmentTaskEdits &&
      (isConditional || isUnconditional) &&
      hasOnlyEnrolmentEdits &&
      onRequestEnrolmentTasksOpen
    ) {
      return {
        label: 'Request enrolment tasks',
        props: { variant: 'solid' },
        onClick: onRequestEnrolmentTasksOpen,
      };
    }

    if (canIssueOffer && !showCompleteAssessment) {
      const hasEverIssued = Boolean(issueDate || reissueDate);
      const isSameType = (allRequirementsResolved && isUnconditional) || (!allRequirementsResolved && isConditional);
      const isReissue = hasEverIssued && isSameType;

      if (allRequirementsResolved) {
        return {
          label: isReissue ? 'Re-issue unconditional offer' : 'Issue unconditional offer',
          props: { variant: 'solid' },
          onClick: onIssueOfferOpen,
        };
      }

      return {
        label: isReissue ? 'Re-issue conditional offer' : 'Issue conditional offer',
        props: { variant: 'solid' },
        onClick: onIssueOfferOpen,
      };
    }

    return undefined;
  }, [
    hasUnresolvedRequirements,
    isInOffersPath,
    status,
    isIssuedOffer,
    hasBeenEdited,
    onIssueOfferOpen,
    onRequestPreOfferTasksOpen,
    onRequestEnrolmentTasksOpen,
    hasPendingPreOfferTasks,
    hasEnrolmentTaskEdits,
    hasOnlyEnrolmentEdits,
    showCompleteAssessment,
    uni,
    userId,
    applicationId,
    issueDate,
    reissueDate,
    navigate,
    allRequirementsResolved,
    isApplied,
    isConditional,
    isUnconditional,
    conditionReqs,
  ]);

  // Legacy alerts are no longer needed (feature is permanently enabled)
  const alerts = useMemo((): SidebarAlert[] => [], []);

  const statusIndicators = useMemo((): StatusIndicator[] => {
    const indicators: StatusIndicator[] = [];

    if (isInOffersPath && isWithdrawn) {
      const isApplicantInitiated =
        statusReason === ApplicantStatusReason.ClosedByApplicant ||
        statusReason === ApplicantStatusReason.WithdrawnOnCompletion;

      if (applicantStatus === ApplicantOfferResponseStatus.Declined && isApplicantInitiated) {
        indicators.push({
          icon: faThumbsDown,
          colorScheme: 'red',
          label: OfferTooltipLabels.applicantDeclined,
          dataSelector: 'offer-declined',
        });
      } else {
        indicators.push({
          icon: faThumbsDown,
          colorScheme: 'red',
          label: OfferTooltipLabels.offerWithdrawn,
          dataSelector: 'offer-withdrawn',
        });
      }
    } else if (isInOffersPath && applicantStatus === ApplicantOfferResponseStatus.Declined) {
      indicators.push({
        icon: faThumbsDown,
        colorScheme: 'red',
        label: OfferTooltipLabels.applicantDeclined,
        dataSelector: 'offer-declined',
      });
    }

    if (isInOffersPath && preOfferTasksRequested && isApplied) {
      indicators.push({
        icon: faCheck,
        colorScheme: 'green',
        variant: 'subtle',
        label: 'Pre-offer tasks requested',
        dataSelector: 'pre-offer-requested',
      });
    }

    if (isInOffersPath && isConditional && (issueDate || reissueDate)) {
      indicators.push({
        icon: faCheck,
        colorScheme: 'green',
        variant: 'subtle',
        label: 'Conditional offer issued',
        dataSelector: 'conditional-offer-issued',
        ...(hasPDFUrl && {
          action: {
            label: 'View',
            href: pdfUrl as string,
          },
        }),
      });
    }

    if (isInOffersPath && isUnconditional && (issueDate || reissueDate)) {
      indicators.push({
        icon: faCheck,
        colorScheme: 'green',
        variant: 'subtle',
        label: 'Unconditional offer issued',
        dataSelector: 'unconditional-offer-issued',
        ...(hasPDFUrl && {
          action: {
            label: 'View',
            href: pdfUrl as string,
          },
        }),
      });
    }

    if (isInOffersPath && hasPendingPreOfferTasks && isApplied) {
      indicators.push({
        icon: faPen,
        colorScheme: 'orange',
        label: 'Edited tasks not requested',
        dataSelector: 'pre-offer-pending',
      });
    }

    if (isInOffersPath && (isConditional || isUnconditional) && hasOnlyEnrolmentEdits) {
      indicators.push({
        icon: faPen,
        colorScheme: 'orange',
        label: 'Edited tasks not requested',
        dataSelector: 'enrolment-pending',
      });
    }

    if (isInOffersPath && isConditional && (hasNonEnrolmentTaskEdits || hasOfferLevelEdits)) {
      indicators.push({
        icon: faPen,
        colorScheme: 'orange',
        label: 'Edited offer not issued',
        dataSelector: 'conditions-not-issued',
      });
    }

    if (isInOffersPath && isUnconditional && hasBeenEdited && !hasOnlyEnrolmentEdits) {
      indicators.push({
        icon: faPen,
        colorScheme: 'orange',
        label: 'Edited offer not issued',
        dataSelector: 'offer-not-issued',
      });
    }

    if (isInOffersPath && showCompleteAssessment) {
      indicators.push({
        icon: faInfo,
        colorScheme: 'gray',
        variant: 'subtle',
        contained: true,
        label: 'Complete assessment to issue unconditional offer',
        dataSelector: 'complete-assessment',
      });
    }

    if (isInOffersPath && hasUnresolvedRequirements) {
      indicators.push({
        icon: faTriangleExclamation,
        colorScheme: 'red',
        label: OfferTooltipLabels.requirementsNotSatisfied(notSatisfiedRequirementsCount),
        dataSelector: 'issued-date',
      });
    }

    if (isInProgrammesPath && !(isWithdrawn || isRejected) && !isNotSubmitted) {
      indicators.push({
        icon: faInfo,
        colorScheme: 'gray',
        label: 'Open a programme or create one',
        dataSelector: 'primary',
      });
    }

    return indicators;
  }, [
    isInOffersPath,
    isInProgrammesPath,
    isIssuedOffer,
    isConditional,
    isUnconditional,
    issueDate,
    hasBeenEdited,
    isWithdrawn,
    isRejected,
    isNotSubmitted,
    reissueDate,
    hasUnresolvedRequirements,
    hasPendingPreOfferTasks,
    hasEnrolmentTaskEdits,
    hasOnlyEnrolmentEdits,
    hasNonEnrolmentTaskEdits,
    preOfferTasksRequested,
    showCompleteAssessment,
    isApplied,
    applicantStatus,
    statusReason,
    notSatisfiedRequirementsCount,
    pdfUrl,
    hasPDFUrl,
  ]);

  const buttons = useMemo((): ActionButton[] => {
    const _buttons: ActionButton[] = [];

    // no action buttons if the current user can't edit an offer
    if (!canEditOffer || isAuthorisationDenied) return _buttons;

    // Only add buttons if we should show them based on application state
    if ((isWithdrawn || isRejected) && isInOffersPath) {
      // Buttons for withdrawn/rejected offers
      _buttons.push({
        label: 'Reinstate offer',
        props: { 'data-selector': 'reinstate-offer-button' },
        onClick: onReinstateOfferOpen,
      });
    } else if (!isNotSubmitted) {
      // Buttons for non-withdrawn/rejected and submitted offers
      if (
        !hasUnresolvedRequirements &&
        !isInProgrammesPath &&
        primaryActionButton &&
        (!isInOffersPath || !isIssuedOffer || hasBeenEdited || hasOnlyEnrolmentEdits)
      ) {
        _buttons.push({
          ...primaryActionButton,
          isPrimary: true,
          props: {
            ...primaryActionButton?.props,
          },
        });
      }

      if (isInOffersPath) {
        const withdrawLabel = 'Withdraw offer';
        const rejectLabel = 'Reject offer';
        _buttons.push({
          label: isIssuedOffer ? withdrawLabel : rejectLabel,
          props: { colorScheme: 'red', isDisabled: !offer?.id, 'data-selector': 'reject-offer-button' },
          onClick: onWithdrawRejectOpen,
        });
      }

      _buttons.push({
        label: 'More actions',
        props: { rightIcon: <Icon icon={faAngleDown} /> },
        isDropdown: true,
        menuItems: moreActionsMenuItems,
      });
    }

    return _buttons;
  }, [
    hasUnresolvedRequirements,
    isWithdrawn,
    isRejected,
    isInOffersPath,
    isInProgrammesPath,
    isIssuedOffer,
    hasBeenEdited,
    hasOnlyEnrolmentEdits,
    isNotSubmitted,
    primaryActionButton,
    onReinstateOfferOpen,
    onWithdrawRejectOpen,
    moreActionsMenuItems,
    offer?.changeStatusLoading,
    offer?.id,
    canEditOffer,
    isAuthorisationDenied,
  ]);

  return {
    alerts,
    statusIndicators,
    buttons,
    unlinkEmailModalProps: {
      isOpen: isUnlinkEmailOpen,
      onOpen: onUnlinkEmailOpen,
      onClose: onUnlinkEmailClose,
    },
    isInOffersPath,
  };
};
